import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dismiss16Regular } from '@fluentui/react-icons';
import useMicVisualizer from '../../../hooks/useMicVisualizer';
import styles from './index.module.css';

const BAR_COUNT = 10;

const formatElapsed = seconds => {
    const mins = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
    const secs = Math.floor(seconds % 60)
        .toString()
        .padStart(2, '0');
    return `${mins}:${secs}`;
};

const RecordingPopover = ({ active, onStop }) => {
    const { audioLevel, isSpeaking, startMic, stopMic } = useMicVisualizer();
    const [elapsed, setElapsed] = useState(0);
    const sessionActiveRef = useRef(false);
    const bars = useMemo(() => Array.from({ length: BAR_COUNT }, (_, i) => i), []);

    useEffect(() => {
        if (!active) {
            if (sessionActiveRef.current) {
                stopMic({ playSound: true });
                sessionActiveRef.current = false;
            }
            setElapsed(0);
            return undefined;
        }

        sessionActiveRef.current = true;
        let cancelled = false;
        startMic().catch(() => {
            if (!cancelled) sessionActiveRef.current = false;
        });

        const startedAt = Date.now();
        const timerId = window.setInterval(() => {
            setElapsed(Math.floor((Date.now() - startedAt) / 1000));
        }, 250);

        return () => {
            cancelled = true;
            window.clearInterval(timerId);
            if (sessionActiveRef.current) {
                stopMic({ playSound: true });
                sessionActiveRef.current = false;
            }
        };
    }, [active, startMic, stopMic]);

    if (!active) return null;

    return (
        <div className={styles.recordingPopover} role="status" aria-live="polite">
            <div className={styles.recordingPopoverInner}>
                <span className={styles.recordingDot} aria-hidden="true" />
                <span className={styles.recordingTimer}>{formatElapsed(elapsed)}</span>
                <div className={styles.recordingWaves} aria-hidden="true">
                    {bars.map(index => {
                        const waveOffset = Math.sin((index + 1) * 0.95) * 0.2;
                        const height = isSpeaking
                            ? Math.max(0.2, Math.min(1, audioLevel + waveOffset))
                            : 0.18 + (index % 4) * 0.08;
                        return (
                            <span
                                key={index}
                                className={`${styles.recordingBar} ${
                                    isSpeaking ? styles.recordingBarActive : styles.recordingBarIdle
                                }`}
                                style={{ transform: `scaleY(${height})` }}
                            />
                        );
                    })}
                </div>
                <span className={styles.recordingLabel}>{isSpeaking ? 'Listening…' : 'Speak now'}</span>
                <button type="button" className={styles.recordingStopBtn} onClick={onStop} aria-label="Stop recording">
                    <Dismiss16Regular />
                </button>
            </div>
        </div>
    );
};

export default React.memo(RecordingPopover);
